import { getTotalFromContentRange, reduceTokenString } from '@/common/utils/app.utils';
import { baseAppApi } from '@/core/network/baseAppApi';
import { getFirstErrorOrDefault } from '@/core/network/network.utils';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import {
  deduplicateSchedulesResponse,
  sortScheduleResponseByYear,
} from '@/schedules/components/SchedulesDataGrid/schedulesDataGrid.utils';
import tokens from '@/schedules/tokens.json';
import { getBoardId } from '@/core/web-storage/webStorage.utils';

export const RECORDS_PER_PAGE = 25;

export function isValidSchedules(response: SchedulesResponseType) {
  return response && response.schedules?.length >= 0;
}
export function isValidScheduleSummaries(response: SchedulesSummariesResponseItemShape[] | []) {
  return response && response.length >= 0;
}

export type GetSchedulesResponse = {
  error: ErrorResponseItemShape[] | [];
  data: {
    schedules: ScheduleResponseItemShape[];
  };
};

export type GetScheduleSummariesResponse = {
  error: ErrorResponseItemShape[] | [];
  data: {
    scheduleSummaries: SchedulesSummariesResponseItemShape[];
  };
};

export type SchedulesResponseType = {
  schedules: ScheduleResponseItemShape[] | [];
  total: number;
};

export type ErrorResponseItemShape = {
  exceptionMessage: string;
  context: string;
};

export interface SchedulesSummariesResponseItemShape {
  startYear: number;
  startMonth: string;
  endMonth: string;
}

export type ScheduleResponseItemShape = {
  scheduleId: number;
  startYear: number;
  name: string;
  daysInYear: number;
  insertionTimestamp: string;
  insertionUser: string;
  months: Array<ScheduleMonthType>;
  editable?: boolean;
};

export type ScheduleMonthType = {
  scheduleMonthId: number;
  employmentMonth: string;
  days: number;
  employerId: number;
};

export type SchedulesRequest = {
  page?: number;
  pageSize?: number;
  startYear?: number;
};

export type MutateExistingShcheduleResponseType = {
  data: [];
  errors: ErrorResponseItemShape[] | [];
};
export type MutateNewShcheduleResponseType = {
  data: number;
  errors: ErrorResponseItemShape[] | [];
};

export type DeleteScheduleRequestType = {
  scheduleId: number;
};

export type ScheduleMonthToBeCreateAndUpdateShape = {
  employmentMonth: string;
  days: number;
};

export type ScheduleToBeCreateAndUpdateShape = {
  startYear: number;
  name: string;
  daysInYear: number;
  months: Array<ScheduleMonthToBeCreateAndUpdateShape>;
};

export type UpdateScheduleRequestType = {
  scheduleId: number;
  schedule: ScheduleToBeCreateAndUpdateShape;
};

export type CreateScheduelRequestType = {
  schedule: ScheduleToBeCreateAndUpdateShape;
};

export const INITIAL_SCHEDULES_PAGE_FILTER = {
  page: 0,
  pageSize: RECORDS_PER_PAGE,
};

const schedulesApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getSchedulesSummaries: build.query<SchedulesSummariesResponseItemShape[], void>({
      query() {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules/index`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: GetScheduleSummariesResponse) {
        const inner = response?.data?.scheduleSummaries;
        if (Array.isArray(inner) && inner.length > 0) {
          const unique = deduplicateSchedulesResponse(inner);
          response.data.scheduleSummaries = sortScheduleResponseByYear(unique);
          return inner;
        } else {
          return [];
        }
      },
      transformErrorResponse(response): string {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR_FETCHING'], 'schedules summaries'),
        );
      },
      providesTags: ['SCHEDULES_SUMMARIES'],
    }),
    getSchedulesDetails: build.query<SchedulesResponseType, SchedulesRequest>({
      query({ page = 0, pageSize = RECORDS_PER_PAGE, startYear }) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules`,
          params: { pageNum: page + 1, pageSize, startYear },
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: GetSchedulesResponse, meta) {
        if (response?.data?.schedules.length === 0) {
          return {
            schedules: [],
            total: 0,
          };
        }
        const range = meta?.response?.headers.get('Content-Range');
        if (!range) {
          throw new Error('Content-Range not found');
        }

        const total = getTotalFromContentRange(range);
        if (!total || total < 0) {
          throw new Error('Invalid Content-Range value');
        }
        return {
          schedules: response.data.schedules,
          total,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR_FETCHING'], 'schedules details'),
        );
      },
      providesTags: ['SCHEDULES'],
    }),
    createSchedule: build.mutation<MutateNewShcheduleResponseType, CreateScheduelRequestType>({
      query({ schedule }) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules`,
          method: 'POST',
          body: {
            ...schedule,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'creating new'),
        );
      },
      invalidatesTags: ['SCHEDULES', 'SCHEDULES_SUMMARIES', 'UNACCEPTED_BATCHES'],
    }),
    updateSchedule: build.mutation<MutateExistingShcheduleResponseType, UpdateScheduleRequestType>({
      query({ scheduleId, schedule }) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules/${scheduleId}`,
          method: 'POST',
          body: {
            ...schedule,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'updating'),
        );
      },
      invalidatesTags: ['SCHEDULES', 'SCHEDULES_SUMMARIES', 'UNACCEPTED_BATCHES'],
    }),
    deleteSchedule: build.mutation<MutateExistingShcheduleResponseType, DeleteScheduleRequestType>({
      query({ scheduleId }) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules/${scheduleId}`,
          method: 'DELETE',
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(
          response,
          reduceTokenString(tokens['BANNER_ERROR'], 'deleting'),
        );
      },
      invalidatesTags: ['SCHEDULES', 'SCHEDULES_SUMMARIES', 'UNACCEPTED_BATCHES'],
    }),
  }),
  overrideExisting: false,
});

export { schedulesApi };
